import React, { useEffect, useState } from "react";
import ProjectsTab from "./projectTab.js/projectTab";
import Projectdetails from "./projectdetails/projectdetails";
import MentorRequests from "./mentor/mentorlist";

const API_BASE = "https://api.hfadigitalcreations.com";

const Requests = () => {
  const [activeTab, setActiveTab] = useState("Jobs");
  const [jobs, setJobs] = useState([]);
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [search, setSearch] = useState("");

  // Modal states
  const [selectedJob, setSelectedJob] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Loading state for actions
  const [loadingJobId, setLoadingJobId] = useState(null);

  // Fetch jobs on mount
  useEffect(() => {
    if (activeTab === "Jobs") {
      fetchJobs();
    }
  }, [activeTab]);

  const fetchJobs = async () => {
    try {
      const res = await fetch(`${API_BASE}/postjob`);
      const data = await res.json();
      if (data.status) {
        setJobs(data.data);
        setFilteredJobs(data.data);
      }
    } catch (err) {
      console.error("Error fetching jobs:", err);
    }
  };

  // Fetch single job details
  const fetchSingleJob = async (id) => {
    try {
      const res = await fetch(`${API_BASE}/postjob/${id}`);
      const data = await res.json();
      if (data.status) {
        setSelectedJob(data.data);
        setIsModalOpen(true);
      } else {
        alert(data.message);
      }
    } catch (err) {
      console.error("Error fetching job details:", err);
    }
  };

  // Handle search
  useEffect(() => {
    if (!search.trim()) {
      setFilteredJobs(jobs);
    } else {
      setFilteredJobs(
        jobs.filter((job) =>
          job.jobtitle.toLowerCase().includes(search.toLowerCase())
        )
      );
    }
  }, [search, jobs]);

  // Approve job
  const approveJob = async (job) => {
    setLoadingJobId(job.id);
    try {
      const res = await fetch(`${API_BASE}/postjob/approve`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: job.id, user_id: job.user_id }),
      });
      const data = await res.json();
      if (data.status) {
        fetchJobs();
      } else {
        alert(data.message);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingJobId(null);
    }
  };

  // Decline job (you must create decline API same as approve but update `is_approved = 2`)
  const declineJob = async (job) => {
    setLoadingJobId(job.id);
    try {
      const res = await fetch(`${API_BASE}/postjob/decline`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: job.id, user_id: job.user_id }),
      });
      const data = await res.json();
      if (data.status) {
        fetchJobs();
      } else {
        alert(data.message);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingJobId(null);
    }
  };

  // Export CSV manually
  const exportCSV = () => {
    if (!jobs.length) {
      alert("No data to export");
      return;
    }

    const headers = [
      "ID",
      "Job Title",
      "Company",
      "Category",
      "Work Type",
      "Salary From",
      "Salary To",
      "Deadline",
      "Approval Status",
    ];

    const rows = jobs.map((job) => [
      job.id,
      job.jobtitle,
      job.company_name,
      job.category,
      job.worktype,
      job.salary_from,
      job.salary_to,
      job.applicationdeadline,
      job.is_approved === "1"
        ? "Approved"
        : job.is_approved === "2"
        ? "Rejected"
        : "Pending",
    ]);

    const csvContent = [headers, ...rows]
      .map((row) =>
        row
          .map((item) =>
            typeof item === "string" && item.includes(",") ? `"${item}"` : item
          )
          .join(",")
      )
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "jobs_export.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="p-6">
      {/* Tabs */}
      <div className="flex gap-6 border-b mb-4">
        {[
          "Jobs",
          "Projects",
          "Events",
          "Project Details",
          "Mentor Requests",
        ].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`pb-2 font-medium ${
              activeTab === tab
                ? "text-blue-600 border-b-2 border-blue-600"
                : "text-gray-500"
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* Jobs tab */}
      {activeTab === "Jobs" && (
        <>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Job Requests</h2>
            <div className="flex gap-2">
              <button className="border px-4 py-2 rounded hover:bg-gray-100">
                Filter
              </button>
              <button className="border px-4 py-2 rounded hover:bg-gray-100">
                Date Range
              </button>
              <button
                onClick={exportCSV}
                className="border px-4 py-2 rounded bg-green-500 text-white hover:bg-green-600"
              >
                Export CSV
              </button>
            </div>
          </div>

          {/* Search */}
          <input
            type="text"
            placeholder="Search jobs..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="border rounded w-full p-2 mb-4"
          />

          {/* Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredJobs.map((job) => (
              <div
                key={job.id}
                className="border rounded p-4 bg-white shadow-sm flex flex-col justify-between"
              >
                <div>
                  <h3 className="font-semibold text-lg">{job.jobtitle}</h3>
                  <p className="text-gray-500 text-sm">{job.company_name}</p>
                  <span
                    className={`inline-block mt-2 text-xs px-2 py-1 rounded ${
                      job.is_approved === "1"
                        ? "bg-green-100 text-green-700"
                        : job.is_approved === "2"
                        ? "bg-red-100 text-red-700"
                        : "bg-yellow-100 text-yellow-700"
                    }`}
                  >
                    {job.is_approved === "1"
                      ? "Approved"
                      : job.is_approved === "2"
                      ? "Rejected"
                      : "Pending"}
                  </span>
                </div>

                {/* Actions */}
                <div className="flex justify-between items-center mt-4">
                  <button
                    onClick={() => fetchSingleJob(job.id)}
                    className="text-blue-600 hover:underline"
                  >
                    Quick View
                  </button>
                  <div className="flex gap-2">
                    {/* Show Approve only if not approved */}
                    {job.is_approved !== "1" && (
                      <button
                        onClick={() => approveJob(job)}
                        disabled={loadingJobId === job.id}
                        className="bg-green-500 text-white px-3 py-1 rounded hover:bg-green-600 disabled:opacity-50"
                      >
                        Approve
                      </button>
                    )}

                    {/* Show Decline only if not declined */}
                    {job.is_approved !== "2" && (
                      <button
                        onClick={() => declineJob(job)}
                        disabled={loadingJobId === job.id}
                        className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600 disabled:opacity-50"
                      >
                        Decline
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Placeholder for other tabs */}
      {activeTab === "Projects" && (
        <div className="text-gray-500 text-center mt-10">
          <ProjectsTab />
        </div>
      )}

      {activeTab === "Events" && (
        <div className="text-gray-500 text-center mt-10">
          Event Requests will be displayed here.
        </div>
      )}

      {activeTab === "Project Details" && (
        <div className="text-gray-500 text-center mt-10">
          <Projectdetails />
        </div>
      )}
      {activeTab === "Mentor Requests" && <MentorRequests />}

      {/* Modal for Quick View */}
      {isModalOpen && selectedJob && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded p-6 w-full max-w-md shadow-lg relative">
            <h3 className="text-lg font-semibold mb-2">
              {selectedJob.jobtitle}
            </h3>
            <p className="text-gray-500 mb-4">{selectedJob.company_name}</p>
            <p className="text-sm mb-2">
              <strong>Category:</strong> {selectedJob.category}
            </p>
            <p className="text-sm mb-2">
              <strong>Description:</strong> {selectedJob.jobdescription}
            </p>
            <p className="text-sm mb-2">
              <strong>Work Type:</strong> {selectedJob.worktype}
            </p>
            <p className="text-sm mb-2">
              <strong>Salary:</strong> {selectedJob.salary_from} -{" "}
              {selectedJob.salary_to}
            </p>
            <p className="text-sm mb-4">
              <strong>Deadline:</strong> {selectedJob.applicationdeadline}
            </p>

            <button
              onClick={() => setIsModalOpen(false)}
              className="absolute top-2 right-2 text-gray-500 hover:text-gray-700"
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Requests;
